<template>
  <div class="markdown-content" v-html="processedContent"></div>
</template>

<script>
import { marked } from 'marked';
import katex from 'katex';
import 'katex/dist/katex.min.css';

export default {
  name: 'MarkdownWithLatex',
  props: {
    content: {
      type: String,
      required: true
    },
    options: {
      type: Object,
      default: () => ({
        breaks: true,
        gfm: true,
        smartLists: true,
        smartypants: true
      })
    }
  },
  data() {
    return {
      isMarkedReady: false
    };
  },
  created() {
    this.initializeMarked();
  },
  methods: {
    initializeMarked() {
      try {
        marked.setOptions({
          ...this.options,
          sanitize: false,
          mangle: false,
          headerIds: false
        });

        const renderer = new marked.Renderer();

        // 处理段落中的公式（包括行内公式）
        renderer.paragraph = (text) => {
          const processedText = this.processMixedFormulas(text);
          return `<p>${processedText}</p>`;
        };

        // 处理代码块中的数学公式
        renderer.code = (code, language) => {
          if (language === 'math') {
            try {
              return katex.renderToString(code, {
                displayMode: true,
                throwOnError: false,
                macros: this.getLatexMacros()
              });
            } catch (e) {
              console.error('数学代码块渲染错误:', e);
              return `<div class="katex-error">${code}</div>`;
            }
          }
          return `<pre><code class="language-${language}">${code}</code></pre>`;
        };

        // 处理表格中的公式
        renderer.tablecell = (content, flags) => {
          const processedContent = this.processMixedFormulas(content);
          const tag = flags.header ? 'th' : 'td';
          const align = flags.align ? ` style="text-align:${flags.align}"` : '';
          return `<${tag}${align}>${processedContent}</${tag}>`;
        };

        // 新增：处理行内文本中的公式（如链接、强调等）
        renderer.text = (text) => {
          return this.processMixedFormulas(text);
        };

        renderer.paragraph = (text) => {
          // 确保段落中的公式会被二次处理
          const processedText = this.processMixedFormulas(text);
          return `<p>${processedText}</p>`;
        };

        marked.use({ renderer });
        this.isMarkedReady = true;
      } catch (err) {
        console.error('marked 初始化失败:', err);
      }
    },

    getLatexMacros() {
      return {
        "\\Label": "\\text{Label}",
        "\\WorkCond": "\\text{WorkCond}",
        "\\AlgVer": "\\text{AlgVer}",
        "\\SensorType": "\\text{SensorType}",
        "\\Wgt": "\\text{Wgt}",
        "\\Flt": "\\text{Flt}",
        "\\Tol": "\\text{Tol}",
        "\\FWMain": "\\text{FWMain}",
        "\\Pat": "\\text{Pat}",
        "\\CompChk": "\\text{CompChk}",
        "\\HWID": "\\text{HWID}",
        "\\Set": "\\text{Set}",


        // 数学集合
        "\\RR": "\\mathbb{R}",
        "\\CC": "\\mathbb{C}",
        "\\ZZ": "\\mathbb{Z}",
        "\\NN": "\\mathbb{N}",
        "\\QQ": "\\mathbb{Q}",
        "\\PP": "\\mathbb{P}",
        "\\EE": "\\mathbb{E}",
        "\\FF": "\\mathbb{F}",
        "\\HH": "\\mathbb{H}",
        "\\OO": "\\mathbb{O}",

        // 希腊字母
        "\\eps": "\\varepsilon",
        "\\phi": "\\varphi",
        "\\theta": "\\vartheta",
        "\\kappa": "\\varkappa",
        "\\rho": "\\varrho",

        // 运算符
        "\\abs": ["\\left|#1\\right|", 1],
        "\\norm": ["\\left\\|#1\\right\\|", 1],
        "\\floor": ["\\left\\lfloor#1\\right\\rfloor", 1],
        "\\ceil": ["\\left\\lceil#1\\right\\rceil", 1],
        "\\set": ["\\left\\{#1\\right\\}", 1],
        "\\paren": ["\\left(#1\\right)", 1],
        "\\brack": ["\\left[#1\\right]", 1],
        "\\inner": ["\\left\\langle#1\\right\\rangle", 1],
        "\\eval": ["\\left.#1\\right|_{#2}", 2],
        "\\pd": ["\\frac{\\partial #1}{\\partial #2}", 2],
        "\\pdd": ["\\frac{\\partial^2 #1}{\\partial #2^2}", 2],
        "\\dv": ["\\frac{d #1}{d #2}", 2],
        "\\ddv": ["\\frac{d^2 #1}{d #2^2}", 2],
        "\\dd": "\\mathrm{d}",
        "\\di": "\\mathrm{i}",
        "\\de": "\\mathrm{e}",

        // 概率与统计
        "\\E": "\\mathbb{E}",
        "\\Var": "\\mathrm{Var}",
        "\\Cov": "\\mathrm{Cov}",
        "\\Corr": "\\mathrm{Corr}",
        "\\SD": "\\mathrm{SD}",
        "\\SE": "\\mathrm{SE}",
        "\\iid": "\\stackrel{\\text{i.i.d.}}{\\sim}",
        "\\indep": "\\stackrel{\\text{indep.}}{\\sim}",

        // 线性代数
        "\\tr": "\\mathrm{tr}",
        "\\rank": "\\mathrm{rank}",
        "\\diag": "\\mathrm{diag}",
        "\\Span": "\\mathrm{Span}",
        "\\Ker": "\\mathrm{Ker}",
        "\\Img": "\\mathrm{Im}",
        "\\Null": "\\mathrm{Null}",
        "\\Col": "\\mathrm{Col}",
        "\\Row": "\\mathrm{Row}",
        "\\det": "\\mathrm{det}",

        // 数学函数
        "\\sgn": "\\mathrm{sgn}",
        "\\Re": "\\mathrm{Re}",
        "\\Im": "\\mathrm{Im}",
        "\\arg": "\\mathrm{arg}",
        "\\res": "\\mathrm{res}",

        // 特殊符号
        "\\given": ["\\mathrel{\\Big|}", 0],
        "\\lcolon": "\\mathpunct{:}",
        "\\rcolon": "\\mathpunct{:}",
        "\\coloneqq": "\\mathrel{:=}",
        "\\eqqcolon": "\\mathrel{=:}",

        // 物理符号
        "\\unit": ["\\mathrm{#1}", 1],
        "\\e": "\\times10^{#1}",
        "\\angstrom": "\\mathrm{\\AA}",
        "\\micro": "\\mu",
        "\\ohm": "\\Omega",

        // 化学符号
        "\\chem": ["\\mathrm{#1}", 1],
        "\\Ox": "\\mathrm{Ox}",
        "\\Red": "\\mathrm{Red}",

        // 量子力学
        "\\bra": ["\\langle #1 |", 1],
        "\\ket": ["| #1 \\rangle", 1],
        "\\braket": ["\\langle #1 | #2 \\rangle", 2],
        "\\op": ["\\hat{#1}", 1],
        "\\Op": ["\\hat{\\mathbf{#1}}", 1],

        // 常微分方程
        "\\Lap": "\\mathcal{L}",
        "\\Fourier": "\\mathcal{F}",
        "\\Ztrans": "\\mathcal{Z}",

        // 向量和矩阵
        "\\v": ["\\mathbf{#1}", 1],
        "\\m": ["\\mathbf{#1}", 1],
        "\\T": "^{\\mathsf{T}}",
        "\\H": "^{\\mathsf{H}}",
        "\\inv": "^{-1}",
        "\\trans": "^{\\mathsf{T}}",
        "\\herm": "^{\\mathsf{H}}",

        // 逻辑符号
        "\\lnot": "\\neg",
        "\\land": "\\wedge",
        "\\lor": "\\vee",
        "\\limp": "\\rightarrow",
        "\\liff": "\\leftrightarrow",
        "\\top": "\\top",
        "\\bot": "\\bot",
        "\\forces": "\\Vdash",

        // 范畴论
        "\\cat": ["\\mathbf{#1}", 1],
        "\\Obj": "\\mathrm{Ob}",
        "\\Hom": "\\mathrm{Hom}",
        "\\End": "\\mathrm{End}",
        "\\Aut": "\\mathrm{Aut}",
        "\\id": "\\mathrm{id}",

        // 交换图
        "\\commsq": ["\\begin{matrix} #1 \\\\ #2 \\end{matrix}", 2],
        "\\pullback": ["\\begin{matrix} #1 \\\\ #2 \\end{matrix}", 2],
        "\\pushout": ["\\begin{matrix} #1 \\\\ #2 \\end{matrix}", 2],

        // 其他
        "\\defeq": "\\stackrel{\\text{def}}{=}",
        "\\eqdef": "\\stackrel{\\text{def}}{=}",
        "\\st": "\\text{ s.t. }",
        "\\wrt": "\\text{ w.r.t. }",
        "\\ie": "\\text{ i.e. }",
        "\\eg": "\\text{ e.g. }",
        "\\etal": "\\text{ et al. }",
        "\\etc": "\\text{ etc. }",
        "\\viz": "\\text{ viz. }",
        "\\cf": "\\text{ cf. }",
        "\\approxeq": "\\stackrel{\\sim}{\\approx}",
        "\\const": "\\mathrm{const}",
        "\\diam": "\\diamond",

        // 确保这些宏存在
        "\\T": "T",
        "\\f": "f",
        "\\SNR": "\\text{SNR}",
        "\\thresh": "\\text{thresh}",
        "\\min": "\\min",
        "\\max": "\\max",
        "\\text": "\\textrm",
        "\\forall": "\\forall",
        "\\quad": "\\qquad",
        "\\in": "\\in",
        // 添加对 > 符号的支持（HTML转义问题）
        "\\gt": ">",
        // 可选：定义自定义宏简化公式
        "\\norm": "\\text{norm}",
        "\\min": "\\text{min}",
        "\\max": "\\text{max}"
      };
    },

    processMixedFormulas(text) {
      const preprocessed = this.preprocessText(text);
      const blockParts = preprocessed.split(/(\$\$[^$]+\$\$)/g);

      let result = '';
      for (let i = 0; i < blockParts.length; i++) {
        if (i % 2 === 1) {
          // 处理块级公式（$$...$$）
          const formula = blockParts[i].slice(2, -2);
          result += this.renderKatex(formula, true);
        } else {
          // 处理行内公式（$...$）
          const inlineParts = blockParts[i].split(/(\$[^$\n]+\$)/g);
          for (let j = 0; j < inlineParts.length; j++) {
            if (j % 2 === 1) {
              const formula = inlineParts[j].slice(1, -1);
              result += this.renderKatex(formula, false);
            } else {
              result += inlineParts[j];
            }
          }
        }
      }

      return result;
    },

    // 公式渲染方法（同时支持块级和行内）
    renderKatex(formula, displayMode) {
      try {
        const cleaned = this.cleanFormula(formula);
        return katex.renderToString(cleaned, {
          displayMode,
          throwOnError: false,
          strict: false, // 关闭严格模式（不检查文本是否在数学模式）
          macros: this.getLatexMacros()
        });
      } catch (e) {
        console.error('KaTeX 渲染错误:', e);
        const tag = displayMode ? 'div' : 'span';
        return `<${tag} class="katex-error">${displayMode ? '$$' : '$'}${formula}${displayMode ? '$$' : '$'}</${tag}>`;
      }
    },

    cleanMathTags(text) {
      return text
        // 移除KaTeX相关标签（包括嵌套的所有子标签）
        .replace(/<span class="katex".*?>.*?<\/span>/gs, '')
        // 移除MathML标签（<mtext>、<mn>、<math>等）
        .replace(/<\/?m(text|n|ath|row|semantics|annotation|mi|mo)>/g, '')
        // 移除其他孤立HTML标签（如残留的</span>）
        .replace(/<\/?[^>]+(>|$)/g, '')
        // 去除多余空格和换行
        .replace(/\s+/g, ' ')
        .trim();
    },

    // 2. 修正LaTeX公式语法（补全符号和下标）
    fixFormulaSyntax(text) {
      return text
        // 将μ1,3修正为\mu_{1,3}（均值符号）
        .replace(/μ(\d+),(\d+)/g, '\\mu_{$1,$2}')
        // 将σ1,3修正为\sigma_{1,3}（方差符号）
        .replace(/σ(\d+),(\d+)/g, '\\sigma_{$1,$2}')
        // 将εi,j,k修正为\varepsilon_{i,j,k}（残差符号）
        .replace(/ε(\w+),(\w+),(\w+)/g, '\\varepsilon_{$1,$2,$3}')
        // 将10−3修正为10^{-3}（指数格式）
        .replace(/10−(\d+)/g, '10^{-$1}')
        // 将∣修正为|（绝对值符号）
        .replace(/∣/g, '|')
        // 为公式添加$包裹（确保被processMixedFormulas识别为行内公式）
        .replace(/(\\mu|\\sigma|\\varepsilon|\\times|\d+\.\d+×10\^{-3}|[|>])/g, ' $1 ');
    },
    preprocessText(text) {
      let cleaned = this.cleanMathTags(text);
      // 新增：再修正公式语法
      cleaned = this.fixFormulaSyntax(cleaned);
      return cleaned
        .replace(/\\\$/g, '&#36;')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/\\_/g, '_')
        .replace(/\\&/g, '&')
        .replace(/\\%/g, '%')
        .replace(/\\#/g, '#')
        .replace(/\\\{/g, '{')
        .replace(/\\\}/g, '}')
        .replace(/&gt;/g, '>')
        .replace(/&lt;/g, '<')
        .replace(/&amp;/g, '&');
    },

    cleanFormula(formula) {
      return formula
        .replace(/[\n\r]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/\\,/g, ',')
        .replace(/\\~/g, '~')
        .replace(/<em>/g, ' ')
        .replace(/<\/em>/g, ' ')
        .trim();
    }
  },
  computed: {
    processedContent() {
      if (!this.isMarkedReady) return '加载中...';

      try {
        const safeContent = this.content
          .replace(/&(?!#?\w+;)/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');

        return marked.parse(safeContent);
      } catch (e) {
        console.error('内容渲染错误:', e);
        return '<div class="error">内容渲染失败</div>';
      }
    }
  }
};
</script>

<style scoped>
.markdown-content {
  line-height: 1.6;
  word-wrap: break-word;
}

.markdown-content>>>.katex {
  font-size: 1.8em;
  line-height: 1.5;
  font-weight: bold;
}

/* .markdown-content>>>.katex-display {
  margin: 1em 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0.2em 0;
  text-align: center;
}

.markdown-content>>>.katex-display>.katex {
  display: inline-block;
  white-space: nowrap;
  max-width: 100%;
} */

.markdown-content>>>.katex-error {
  color: #d32f2f;
  background-color: #ffebee;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
  white-space: pre-wrap;
}

.markdown-content>>>p {
  margin: 1em 0;
}

.markdown-content>>>table .katex {
  font-size: 1em;
}
</style>